import React, { useState } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  PhoneIcon,
  IdentificationIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import MembersList from '../../components/members/MembersList';
import MemberForm from '../../components/members/MemberForm';
import MemberDetail from '../../components/members/MemberDetail';

const MembersPage = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');

  // Fetch members data
  const { data: membersData, isLoading, refetch } = useQuery(
    ['members', { page: currentPage, limit: membersPerPage, search: searchTerm, status: statusFilter }],
    async () => {
      const response = await axios.get('/members', {
        params: {
          page: currentPage,
          limit: membersPerPage,
          search: searchTerm,
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
      });
      return response.data;
    }
  );

  // Données fictives enrichies
  const mockMembers = [
    { id: 'M001', memberCode: 'MEM-2023-001', name: 'Ahmed Benaissa', phone: '0551234567', email: 'ahmed.b@email.com', status: 'active', joinDate: '2023-01-15', currentSubscription: { type: 'Mensuel', startDate: '2023-07-01', endDate: '2023-08-01', price: 5000 }, photo: null },
    { id: 'M002', memberCode: 'MEM-2023-002', name: 'Fatima Khelil', phone: '0661234567', email: 'fatima.k@email.com', status: 'active', joinDate: '2023-01-20', currentSubscription: { type: 'Trimestriel', startDate: '2023-06-01', endDate: '2023-09-01', price: 12000 }, photo: null },
    { id: 'M003', memberCode: 'MEM-2023-003', name: 'Karim Messaoud', phone: '0771234567', email: 'karim.m@email.com', status: 'active', joinDate: '2023-02-05', currentSubscription: { type: 'Annuel', startDate: '2023-02-05', endDate: '2024-02-05', price: 40000 }, photo: null },
    { id: 'M004', memberCode: 'MEM-2023-004', name: 'Samia Benarfa', phone: '0551234568', email: 'samia.b@email.com', status: 'inactive', joinDate: '2023-02-10', currentSubscription: null, photo: null },
    { id: 'M005', memberCode: 'MEM-2023-005', name: 'Mohamed Hakim', phone: '0661234568', email: 'mohamed.h@email.com', status: 'active', joinDate: '2023-03-01', currentSubscription: { type: 'Mensuel', startDate: '2023-07-01', endDate: '2023-08-01', price: 5000 }, photo: null },
    { id: 'M006', memberCode: 'MEM-2023-006', name: 'Amina Ferhat', phone: '0771234568', email: 'amina.f@email.com', status: 'suspended', joinDate: '2023-03-15', currentSubscription: { type: 'Mensuel', startDate: '2023-07-01', endDate: '2023-08-01', price: 5000 }, photo: null },
    { id: 'M007', memberCode: 'MEM-2023-007', name: 'Youcef Talbi', phone: '0551234569', email: 'youcef.t@email.com', status: 'active', joinDate: '2023-04-05', currentSubscription: { type: 'Trimestriel', startDate: '2023-07-05', endDate: '2023-10-05', price: 12000 }, photo: null },
    { id: 'M008', memberCode: 'MEM-2023-008', name: 'Naima Kadri', phone: '0661234569', email: 'naima.k@email.com', status: 'active', joinDate: '2023-04-10', currentSubscription: { type: 'Mensuel', startDate: '2023-07-10', endDate: '2023-08-10', price: 5000 }, photo: null },
    { id: 'M009', memberCode: 'MEM-2023-009', name: 'Hassan Boudiaf', phone: '0771234569', email: 'hassan.b@email.com', status: 'active', joinDate: '2023-04-20', currentSubscription: { type: 'Mensuel', startDate: '2023-07-20', endDate: '2023-08-20', price: 5000 }, photo: null },
    { id: 'M010', memberCode: 'MEM-2023-010', name: 'Leila Mebarki', phone: '0551234570', email: 'leila.m@email.com', status: 'active', joinDate: '2023-05-01', currentSubscription: { type: 'Annuel', startDate: '2023-05-01', endDate: '2024-05-01', price: 40000 }, photo: null },
    { id: 'M011', memberCode: 'MEM-2023-011', name: 'Mourad Chaoui', phone: '0661234570', email: 'mourad.c@email.com', status: 'suspended', joinDate: '2023-05-10', currentSubscription: { type: 'Mensuel', startDate: '2023-07-10', endDate: '2023-08-10', price: 5000 }, photo: null },
    { id: 'M012', memberCode: 'MEM-2023-012', name: 'Sarah Mansouri', phone: '0771234570', email: 'sarah.m@email.com', status: 'active', joinDate: '2023-05-15', currentSubscription: { type: 'Trimestriel', startDate: '2023-05-15', endDate: '2023-08-15', price: 12000 }, photo: null },
  ];

  // Fusionner les champs firstName/lastName en un champ name pour simplifier l'affichage
  const apiMembers = (membersData?.members || []).map(m => ({
    ...m,
    name: m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim(),
    status: m.status || (m.isActive ? 'active' : 'inactive'),
    joinDate: m.createdAt || m.joinDate
  }));

  // Utiliser les données du serveur ou les données fictives
  const allMembers = apiMembers.length > 0 ? apiMembers : mockMembers;
  const filteredMembers = allMembers.filter(member => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return member.status === 'active';
    if (activeTab === 'suspended') return member.status === 'suspended';
    if (activeTab === 'inactive') return member.status === 'inactive';
    if (activeTab === 'no_subscription') return !member.currentSubscription;
    return true;
  });
  
  const members = filteredMembers.slice((currentPage - 1) * membersPerPage, currentPage * membersPerPage);
  const totalMembers = filteredMembers.length;
  const totalPages = Math.ceil(totalMembers / membersPerPage);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleDelete = async (memberId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
      try {
        await axios.delete(`/members/${memberId}`);
        refetch();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  // Stats des membres
  const stats = [
    {
      title: 'Total Membres',
      value: allMembers.length,
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Membres Actifs',
      value: allMembers.filter(m => m.status === 'active').length,
      icon: UserPlusIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Nouveaux ce mois',
      value: membersData?.newThisMonth || 5,
      icon: CalendarDaysIcon,
      color: 'from-purple-500 to-purple-600',
    },
  ];

  // Répartition par abonnement
  const subscriptionDistribution = [
    { name: 'Mensuel', count: 7, color: 'from-blue-400 to-blue-500' },
    { name: 'Trimestriel', count: 3, color: 'from-purple-400 to-purple-500' },
    { name: 'Annuel', count: 2, color: 'from-green-400 to-green-500' },
    { name: 'Aucun', count: 1, color: 'from-gray-400 to-gray-500' },
  ];

  // Onglets
  const tabs = [
    { id: 'all', name: 'Tous les Membres', icon: UserGroupIcon },
    { id: 'active', name: 'Actifs', icon: CheckCircleIcon },
    { id: 'suspended', name: 'Suspendus', icon: ExclamationTriangleIcon },
    { id: 'inactive', name: 'Inactifs', icon: XCircleIcon },
    { id: 'no_subscription', name: 'Sans Abonnement', icon: CalendarDaysIcon },
  ];

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('members')}
              </h1>
              <p className="text-white/60 mt-2">
                Gestion des membres de votre salle
              </p>
            </div>
            <Link
              to="/members/new"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
            >
              <UserPlusIcon className="h-5 w-5" />
              <span>Nouveau Membre</span>
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCurrentPage(1);
                  }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Répartition par type d'abonnement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <h3 className="text-xl font-semibold text-white mb-4">
              Répartition par Type d'Abonnement
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {subscriptionDistribution.map((item) => (
                <div 
                  key={item.name}
                  className="bg-white/5 rounded-xl p-4 border border-white/10"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-white">{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full">
                    <div 
                      className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                      style={{ width: `${(item.count / allMembers.length * 100).toFixed(1)}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-sm text-white/60">
                    {(item.count / allMembers.length * 100).toFixed(1)}% des membres
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Search and Filters */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Rechercher un membre..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-white/50" />
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actifs</option>
                  <option value="inactive">Inactifs</option>
                  <option value="suspended">Suspendus</option>
                </select>
              </div>
            </div>
          </div>

          {/* Members List */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white/30"></div>
              </div>
            ) : members.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <UserGroupIcon className="h-16 w-16 text-white/30 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Aucun membre trouvé</h3>
                <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                <Link
                  to="/members/new"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
                >
                  <UserPlusIcon className="h-5 w-5" />
                  <span>Ajouter un membre</span>
                </Link>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-white font-semibold">Photo</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Code</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Nom</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Téléphone</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Abonnement</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {members.map((member) => (
                        <motion.tr
                          key={member.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-white/5 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                              {member.photo ? (
                                <img src={member.photo} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <span className="text-white font-semibold text-sm">
                                  {member.name.charAt(0)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-white font-mono text-sm">{member.memberCode}</td>
                          <td className="px-6 py-4 text-white font-medium">{member.name}</td>
                          <td className="px-6 py-4 text-white/80">{member.phone}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              member.status === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : member.status === 'suspended'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {member.status === 'active' ? 'Actif' : 
                               member.status === 'suspended' ? 'Suspendu' : 'Inactif'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-white/80">
                            {member.currentSubscription ? (
                              <span className="text-sm">
                                {member.currentSubscription.type} - 
                                {new Date(member.currentSubscription.endDate).toLocaleDateString('fr-FR')}
                              </span>
                            ) : (
                              <span className="text-red-400">Aucun</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/members/${member.id}`}
                                className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                title="Voir les détails"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Link>
                              <Link
                                to={`/members/${member.id}/edit`}
                                className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                                title="Modifier"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Link>
                              <button
                                onClick={() => handleDelete(member.id)}
                                className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                title="Supprimer"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                    <div className="text-sm text-white/60">
                      Affichage de {(currentPage - 1) * membersPerPage + 1} à {Math.min(currentPage * membersPerPage, totalMembers)} sur {totalMembers} membres
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      } />
      <Route path="/new" element={<MemberForm />} />
      <Route path="/:id" element={<MemberDetail />} />
      <Route path="/:id/edit" element={<MemberForm />} />
    </Routes>
  );
};

export default MembersPage; 